import { useCallback } from 'react';

interface SmoothScrollOptions {
  offset?: number;
  duration?: number;
  easing?: (t: number) => number;
}

const defaultOptions: SmoothScrollOptions = {
  offset: 0,
  duration: 800,
  easing: t => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t) // Ease in-out quad
};

export function useSmoothScroll(options: SmoothScrollOptions = {}) {
  const { offset, duration, easing } = { ...defaultOptions, ...options };

  const scrollTo = useCallback((targetElement: HTMLElement | string | null) => {
    if (!targetElement) return;

    let target: HTMLElement | null = null;

    if (typeof targetElement === 'string') {
      // Remove the # if it exists
      const id = targetElement.startsWith('#') ? targetElement.substring(1) : targetElement;
      target = document.getElementById(id);
    } else {
      target = targetElement;
    }

    if (!target) return;

    const start = window.scrollY || window.pageYOffset;
    const targetPosition = target.getBoundingClientRect().top + start - (offset || 0);
    const startTime = performance.now();

    const step = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / (duration || 1), 1);
      const easedProgress = easing ? easing(progress) : progress;

      window.scrollTo({
        top: start + (targetPosition - start) * easedProgress,
        behavior: 'auto' // We're handling the animation ourselves
      });

      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };

    window.requestAnimationFrame(step);
  }, [offset, duration, easing]);

  // Function to handle anchor link clicks
  const handleLinkClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    
    const href = e.currentTarget.getAttribute('href');
    if (!href || !href.startsWith('#')) return;
    
    scrollTo(href);
  }, [scrollTo]);

  return {
    scrollTo,
    handleLinkClick
  };
}